<template>
  <div v-if="$can('view', 'YachtModel')">
    <v-row dense>
      <v-col
        cols="8"
      >
        <!--<editor-fold description="Basic Details">-->
        <div class="subtitle-1 pa-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              Basic Details
            </v-col>
            <v-col cols="auto">
              <v-btn
                v-if="$can('edit', 'YachtModel')"
                text
                color="grey"
                rounded
                small
                class="ma-0"
              >
                <v-icon
                  left
                  small
                >
                  edit
                </v-icon>
                Edit
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <v-card elevation="10">
          <v-card-text>
            <yacht-data-row
              label="Full Name"
              striped
            >
              {{ yachtModel.full_label }}
            </yacht-data-row>
            <yacht-data-row
              label="Public Label"
              striped
            >
              {{ yachtModel.name }}
            </yacht-data-row>
            <yacht-data-row
              label="Internal Label"
              striped
            >
              {{ yachtModel.label }}
            </yacht-data-row>
            <yacht-data-row
              label="Category"
              striped
            >
              {{ yachtModel.type.label }}
            </yacht-data-row>
            <yacht-data-row
              label="Cabins"
              striped
            >
              {{ yachtModel.cabin_count_description }}
            </yacht-data-row>
            <yacht-data-row
              label="RNOG"
              striped
            >
              {{ yachtModel.berth_count_description }}
            </yacht-data-row>
            <yacht-data-row
              label="Engine"
              striped
            >
              {{ yachtModel.engines_description }}
            </yacht-data-row>
            <yacht-data-row
              label="Dimensions"
              striped
            >
              LOA {{ yachtModel.length | fromMetric('m', 2) }} &middot; Beam {{ yachtModel.beam | fromMetric('m', 2) }} &middot; Draught {{ yachtModel.draught | fromMetric('m', 2) }}
            </yacht-data-row>
            <yacht-data-row
              label="Mast height"
              striped
            >
              {{ yachtModel.mast_height | fromMetric('m', 2) }}
            </yacht-data-row>
            <yacht-data-row
              label="Sail area main & furling genoa"
              striped
            >
              {{ yachtModel.main_sail_area | fromMetric('m2', 2) }}
            </yacht-data-row>
            <yacht-data-row
              label="Light displacement"
              striped
            >
              {{ yachtModel.displacement | fromMetric('kg', 2) }}
            </yacht-data-row>
            <yacht-data-row
              label="Toilletes"
              striped
            >
              {{ yachtModel.toilette_count }}
            </yacht-data-row>
            <yacht-data-row
              label="Steering wheels"
              striped
            >
              {{ yachtModel.steering_wheel_count }}
            </yacht-data-row>
            <yacht-data-row
              label="Fuel tank(s)"
              striped
            >
              {{ yachtModel.fuel_tanks_description }} l
            </yacht-data-row>
            <yacht-data-row
              label="Water tank(s)"
              striped
            >
              {{ yachtModel.water_tanks_description }} l
            </yacht-data-row>
          </v-card-text>
        </v-card>
        <!--</editor-fold>-->
      </v-col>
      <v-col
        cols="8"
      >
        <!--<editor-fold description="Finance Details">-->
        <div class="subtitle-1 pa-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              Country Availability
            </v-col>
          </v-row>
        </div>
        <v-card elevation="10">
          <v-card-text>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>
                    Country
                  </th>
                  <th>
                    Configured Equipment
                  </th>
                  <th>
                    Yearly Income
                  </th>
                  <th>
                    Yearly Costs
                  </th>
                  <th>
                    Sale Price
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="country in countries"
                  :key="country.id"
                >
                  <td>
                    <flag
                      :iso="country.code"
                      :title="country.name"
                      :squared="false"
                      class="radius-3 mr-3 elevation-5"
                      :style="[ ...!country.included && { opacity: 0.2, filter: 'grayscale(100%)' } ]"
                    />
                    <span :style="[ ...!country.included && { opacity: 0.3 } ]">{{ country.name }}</span>
                  </td>
                  <td>
                    <div
                      v-if="country.yachtModel"
                      :class="{ 'error--text font-weight-bold': (country.yachtModel.pivot.selected_equipment_categories_count / availableEquipmentPackCategories) <= 0.5 }"
                    >
                      {{ country.yachtModel.pivot.selected_equipment_categories_count }} / {{ availableEquipmentPackCategories }}
                    </div>
                  </td>
                  <td>
                    <div v-if="country.yachtModel">
                      <span v-if="country.yachtModel.pivot.yearly_income">{{ country.yachtModel.pivot.currency }} {{ country.yachtModel.pivot.yearly_income | currency('') }}</span>
                      <span
                        v-else
                        class="error--text font-weight-bold"
                      >
                        N/A
                      </span>
                    </div>
                  </td>
                  <td>
                    <div v-if="country.yachtModel">
                      <span v-if="country.yachtModel.pivot.yearly_expense">{{ country.yachtModel.pivot.currency }} {{ country.yachtModel.pivot.yearly_expense | currency('') }}</span>
                      <span
                        v-else
                        class="error--text font-weight-bold"
                      >
                        N/A
                      </span>
                    </div>
                  </td>
                  <td
                    style="width: 0px"
                    class="text-no-wrap"
                  >
                    <span v-if="country.yachtModel">
                      {{ yachtModel.currency }} {{ country.yachtModel.pivot.total_sale_price | currency('') }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
        <!--</editor-fold>-->
      </v-col>
    </v-row>
  </div>
</template>

<script>
import YachtDataRow from '@/components/business/yachts/details/YachtDataRow'
import { sortBy } from 'lodash'

export default {
  name: 'YachtModelDetailsDashboard',
  components: { YachtDataRow },
  props: {
    yachtModel: {
      type: Object,
      default: () => {
      }
    }
  },
  data() {
    return {}
  },
  computed: {
    countries() {
      return sortBy(this.$store.getters['settings/charteringCountries'].data, 'name').map(c => {
        let yachtModelCountry = this.yachtModel.countries.find(pc => c.id === pc.id)

        return {
          ...c,
          included: !!yachtModelCountry,
          yachtModel: yachtModelCountry
        }
      })
    },
    availableEquipmentPackCategories: function () {
      return this.$store.state.settings.equipmentPackCategories.data.filter(c => !c.is_cdr && !c.is_yard_equipment).length
    }
  },
  created() {
  },
  methods: {}
}
</script>
